import { Link } from "react-router-dom";

import { Layout } from "@/components/layout";
import { type PostListItem, usePosts } from "@/lib/queries/use-posts";
import { useSiteContent } from "@/lib/queries/use-site-content";

function formatDate(dateString: string | null): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" }).toUpperCase();
}

function PostRow({ post }: { post: PostListItem }) {
  return (
    <Link
      to={`/writing/${post.slug}`}
      className="group flex items-baseline justify-between py-5 border-b border-border hover:bg-card -mx-4 px-4 transition-colors"
    >
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <h3 className="text-[17px] font-bold text-text-primary group-hover:text-primary transition-colors">
            {post.title}
          </h3>
          {post.type === "note" && (
            <span className="text-[10px] text-text-faint tracking-caps uppercase px-2 py-0.5 border border-border">
              Note
            </span>
          )}
        </div>
        {post.excerpt && <p className="text-[13px] text-text-tertiary mt-1">{post.excerpt}</p>}
      </div>
      <time className="text-[11px] text-text-faint tracking-wider ml-8 shrink-0">
        {formatDate(post.published_at)}
      </time>
    </Link>
  );
}

export function Writing() {
  const { data: posts, isLoading, error } = usePosts();
  const { data: content } = useSiteContent();

  // Content with defaults
  const title = content?.writing_title || "Writing";
  const description =
    content?.writing_description ||
    "Essays and notes on engineering, startups, and whatever else I find interesting.";

  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-8 pt-20 pb-16">
        <p className="text-[11px] text-text-muted tracking-wide-caps uppercase mb-6">Archive</p>
        <h1 className="text-[44px] font-bold text-primary leading-[1.15] mb-4 tracking-tight">
          {title}
        </h1>
        <div
          className="text-[15px] text-text-secondary leading-relaxed max-w-lg mb-12 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:text-primary [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:opacity-80"
          dangerouslySetInnerHTML={{ __html: description }}
        />

        {isLoading && (
          <div className="border-t border-border py-12">
            <p className="text-text-muted text-center">Loading posts...</p>
          </div>
        )}

        {error && (
          <div className="border-t border-border py-12">
            <p className="text-destructive text-center">
              Failed to load posts. Please try again later.
            </p>
          </div>
        )}

        {posts && posts.length === 0 && (
          <div className="border-t border-border py-12">
            <p className="text-text-muted text-center">No posts yet. Check back soon.</p>
          </div>
        )}

        {posts && posts.length > 0 && (
          <div className="border-t border-border">
            {posts.map((post) => (
              <PostRow key={post.id} post={post} />
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
